import { useEffect, useRef, useState } from "react";
import ReplyOnMessage from "./ReplyOnMessage";
import AnnouncementModal from "../AnnouncementModal";
import { Formik, Form, Field } from "formik";
import SendFileAnimationComponent from "../../assets/lottie/SendFileAnimation/SendFileAnimationComponent";
import { deleteReplyOnMessage, editReplyOnMessage } from "../../apis";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import RowLoader from "../UI/RowLoader";
import SeenIcons from "../Svgs/SeenIcons";
import InfiniteScroll from "react-infinite-scroll-component";
import useGetRepliesForPartner from "./useGetRepliesForPartner";

const MessageRepliers = () => {
  const { messageId } = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editMody, setEditMode] = useState(false);
  const [currentImageReply, setCurrentImageReply] = useState("");
  const [currentReplyId, setCurrentReplyId] = useState("");
  const [newAttachment, setNewAttachment] = useState(null);
  const scrollRef = useRef();
  const topScrollMarker = useRef();
  const {
    replies,
    pagination,
    isLoading,
    fetchReplies,
    loadMoreReplies,
    setPagination,
    setReplies,
  } = useGetRepliesForPartner({
    messageId,
    scrollRef,
  });

  useEffect(() => {
    fetchReplies(1, true);
  }, [messageId]);

  const handleEditOnReply = (replyId, values) => {
    const formData = new FormData();
    formData.append("message", values.message);
    if (newAttachment) formData.append("message_attache", newAttachment);

    setEditLoading(true);
    editReplyOnMessage(replyId, formData, "client")
      .then(() => {
        fetchReplies(1, true)
          .then((result) => {
            if (result) {
              const { repliesData, pagination } = result;
              setReplies(repliesData);
              setPagination(pagination);
            }
          })
          .catch((error) => {
            console.log(error);
          });
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Reply update failed",
          text:
            error?.response?.data?.message ||
            error.message ||
            "Error updating reply",
          confirmButtonText: "Ok",
        });
      })
      .finally(() => {
        setEditLoading(false);
        setEditMode(false);
        setNewAttachment(null);
        setCurrentReplyId(null);
      });
  };

  const handleDeleteReply = (replyId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This reply will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: () => {
        return deleteReplyOnMessage(replyId, "client")
          .then((res) => res.data)
          .catch((error) => {
            Swal.showValidationMessage(
              error?.response?.data?.message ||
                error.message ||
                "Failed to delete the reply"
            );
          });
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire("Deleted!", "The reply has been deleted.", "success");
        fetchReplies(1, true)
          .then((result) => {
            if (result) {
              const { repliesData, pagination } = result;
              setReplies(repliesData);
              setPagination(pagination);
            }
          })
          .catch((error) => {
            console.log(error);
          });
        setEditMode(false);
        setNewAttachment(null);
        setCurrentReplyId(null);
      }
    });
  };

  return (
    <>
      <h4 className="tw-me-auto tw-font-bold tw-text-[17px]">
        Message Replies :
      </h4>
      <article className="tw-mb-4 tw-break-inside tw-px-2 tw-py-1 tw-rounded-xl tw-text-black tw-bg-white tw-flex tw-flex-col tw-bg-clip-border tw-w-full">
        <div
          id="scrollableReplies"
          ref={scrollRef}
          style={{
            overflow: "auto",
            display: "flex",
            flexDirection: "column-reverse",
          }}
          className="tw-max-h-[60vh] tw-overflow-y-auto"
        >
          <div ref={topScrollMarker}></div>

          <InfiniteScroll
            dataLength={replies.length}
            next={loadMoreReplies}
            hasMore={pagination.current_page < pagination.last_page}
            loader={
              isLoading && (
                <span className="tw-absolute tw-top-0 tw-text-center tw-text-gray-400">
                  Loading more...
                </span>
              )
            }
            scrollableTarget="scrollableReplies"
            inverse={true}
          >
            {replies?.length > 0 && (
              <>
                {replies?.map((d) => (
                  <div
                    key={d.id}
                    className={`tw-media tw-flex tw-pb-4 ${
                      d?.isMyReply && "tw-bg-[#eee]"
                    }  tw-p-2 tw-m-2 tw-rounded-md`}
                  >
                    <img
                      className="tw-rounded-full tw-mr-4 tw-max-w-none tw-w-12 tw-h-12"
                      src={d?.userImage ?? "/imgs/1.png"}
                      alt="userImage"
                    />

                    <div className="tw-media-body tw-w-full">
                      <div className="tw-flex max-lg:tw-flex-col">
                        <span className="tw-inline-block tw-text-base tw-font-bold tw-mr-2">
                          {d?.userName}
                        </span>
                        <span className="tw-text-slate-500 tw-flex">
                          {d?.createdAt}
                          {d?.isSeen && d?.isMyReply ? (
                            <span className="tw-ps-2">
                              <SeenIcons />
                            </span>
                          ) : (
                            ""
                          )}
                        </span>

                        {currentReplyId !== d.id &&
                          d?.isMyReply &&
                          !d?.isSeen && (
                            <div className="tw-flex tw-mt-0 tw-ms-auto tw-mb-2">
                              <span
                                onClick={() => {
                                  setEditMode(true);
                                  setCurrentReplyId(d?.id);
                                  setNewAttachment(null);
                                }}
                                className="tw-font-medium hover:tw-text-blue-400 tw-cursor-pointer tw-rounded-lg"
                              >
                                <span className="material-icons">edit</span>
                              </span>
                              <span
                                onClick={() => handleDeleteReply(d.id)}
                                className="tw-px-3 tw-font-medium hover:tw-text-red-700 tw-text-red-500  tw-rounded-lg tw-cursor-pointer"
                              >
                                <span className="material-icons">delete</span>
                              </span>
                            </div>
                          )}
                      </div>

                      {editMody && currentReplyId === d.id ? (
                        <Formik
                          initialValues={{ message: d.message }}
                          onSubmit={(values) => handleEditOnReply(d.id, values)}
                        >
                          {({ handleSubmit }) => (
                            <Form onSubmit={handleSubmit}>
                              <Field
                                name="message"
                                as="textarea"
                                rows={1}
                                className="!tw-h-28 tw-resize-none tw-w-full tw-bg-slate-200 tw-rounded-lg tw-placeholder:tw-text-slate-600 dark:tw-placeholder:tw-text-slate-300 tw-font-medium tw-pr-24 tw-pt-2 tw-pb-2 tw-pl-3"
                                placeholder="Edit your reply"
                              />
                              <div className="">
                                <label
                                  htmlFor={`file-upload-${d.id}`}
                                  className="tw-cursor-pointer !tw-m-0"
                                >
                                  <div className="tw-border tw-flex tw-items-center tw-ps-3 tw-rounded-lg tw-text-[13px]">
                                    <span>Upload New Attachment</span>
                                    <SendFileAnimationComponent className="tw-w-8 tw-h-8" />
                                  </div>
                                </label>
                                <input
                                  id={`file-upload-${d.id}`}
                                  type="file"
                                  accept="image/*"
                                  className="tw-hidden"
                                  onChange={(e) => {
                                    if (e.target.files[0]) {
                                      setNewAttachment(e.target.files[0]);
                                    }
                                  }}
                                />
                              </div>

                              {newAttachment ? (
                                <div className="tw-relative tw-w-1/4 max-xl:tw-w-1/2 tw-h-28 tw-rounded-md tw-overflow-hidden tw-flex-shrink-0 tw-mt-2">
                                  <img
                                    src={URL.createObjectURL(newAttachment)}
                                    alt="newAttachment preview"
                                    className="tw-w-full tw-h-full tw-object-cover tw-rounded-md tw-cursor-pointer"
                                    onClick={() => {
                                      setIsOpen(true);
                                      setCurrentImageReply(d.id);
                                    }}
                                  />
                                  <span
                                    onClick={() => setNewAttachment(null)}
                                    className="material-icons tw-absolute tw-top-0 tw-right-0 tw-text-red-500 hover:tw-text-red-700 tw-cursor-pointer tw-bg-white tw-rounded-full tw-text-base tw-leading-none tw-p-[2px]"
                                  >
                                    close
                                  </span>
                                </div>
                              ) : (
                                <div className="tw-flex tw-justify-between max-md:tw-flex-col max-md:tw-gap-4">
                                  {d?.messageAttache && (
                                    <div className="tw-relative tw-w-1/4 max-xl:tw-w-1/2 tw-h-28 tw-rounded-md tw-overflow-hidden tw-flex-shrink-0 tw-mt-2">
                                      <img
                                        src={d?.messageAttache}
                                        alt="currentAttachment"
                                        className="tw-w-full tw-h-full tw-object-cover tw-rounded-md tw-cursor-pointer"
                                        onClick={() => {
                                          setIsOpen(true);
                                          setCurrentImageReply(d.id);
                                        }}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="tw-flex tw-mt-2 tw-justify-end">
                                <button
                                  type="submit"
                                  className="btn btn-primary btn-sm !tw-rounded-lg ml-auto !tw-w-[100px]"
                                >
                                  {editLoading ? (
                                    <RowLoader paddingStyle="tw-py-0" />
                                  ) : (
                                    "Save"
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditMode(false);
                                    setNewAttachment(null);
                                    setCurrentReplyId(null);
                                  }}
                                  className="tw-bg-gray-300 btn-sm !tw-w-[100px] !tw-rounded-lg"
                                >
                                  Cancel
                                </button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      ) : (
                        <>
                          <p>{d?.message}</p>
                          {d?.messageAttache && (
                            <div className="tw-relative tw-w-1/4 max-xl:tw-w-1/2 tw-h-28 !tw-rounded-md tw-overflow-hidden tw-flex-shrink-0 tw-mt-2">
                              <img
                                onClick={() => {
                                  setIsOpen(true);
                                  setCurrentImageReply(d.id);
                                }}
                                className="tw-w-full tw-h-full tw-object-cover tw-rounded-md tw-cursor-pointer"
                                src={d?.messageAttache}
                                alt="messageAttache"
                              />
                            </div>
                          )}
                        </>
                      )}

                      {d?.messageAttache && currentImageReply === d.id && (
                        <AnnouncementModal
                          isOpen={isOpen}
                          onClose={setIsOpen}
                          imageSrc={
                            newAttachment && currentReplyId === d.id
                              ? URL.createObjectURL(newAttachment)
                              : d?.messageAttache
                          }
                        />
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}
          </InfiniteScroll>
        </div>
        <ReplyOnMessage setPagination={setPagination} setReplies={setReplies} />
      </article>
    </>
  );
};

export default MessageRepliers;
